import React from "react";
import NextScript from "next/script";

/**
 * RudderStack SDK init — the single place the SDK is loaded. Everything else
 * (customAnalytics/rudderStackEvents, RudderStackPageEvents) reuses the resulting
 * window.rudderanalytics instance, so they need no key of their own.
 *
 * The write key is resolved PER UNIVERSITY on the server (university_app_config.
 * rudder_stack_source_write_key → NEXT_PUBLIC_RUDDERSTACK_SOURCE_WRITE_KEY) and passed in
 * as a prop. It must not come from process.env alone: NEXT_PUBLIC_* is inlined at
 * `next build`, so one build can only ever ship ONE key — unusable in multi-tenant, where
 * each tenant needs its own RudderStack source. The env read remains as the standalone
 * fallback (same belt-and-braces as MetaPixel/UpgradChatbot).
 *
 * No key → render nothing: the SDK never loads, window.rudderanalytics stays undefined,
 * and every event helper safely no-ops.
 */

/**
 * The key is interpolated into an inline <script>. It is now admin-editable (DB-backed)
 * rather than a trusted build-time constant, so a value containing a quote or `</script>`
 * could break out of the JS string literal. RudderStack write keys are base62-ish, so
 * anything outside that charset is rejected outright rather than escaped.
 */
const WRITE_KEY_PATTERN = /^[A-Za-z0-9_-]+$/;

const RudderStack = ({ writeKey }: { writeKey?: string | null } = {}) => {
  const resolved =
    (writeKey && writeKey.trim()) ||
    process.env["NEXT_PUBLIC_RUDDERSTACK_SOURCE_WRITE_KEY"] ||
    "";

  if (!resolved || !WRITE_KEY_PATTERN.test(resolved)) {
    if (resolved && process.env.NODE_ENV !== "production") {
      console.warn(
        "[RudderStack] Ignoring malformed source write key — expected only letters, digits, '-' or '_'."
      );
    }
    return null;
  }

  return (
    <>
      <link rel="preconnect" href="https://cdn.rudderlabs.com" />
      <link rel="preconnect" href="https://polyfill-fastly.io" />
      <link rel="preconnect" href="https://upgradbalcvjs.dataplane.rudderstack.com" />
      <NextScript
        id="rudderstack-analytics"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
        __html: `
          !function(){"use strict";window.RudderSnippetVersion="3.2.0";var e="rudderanalytics";window[e]||(window[e]=[])
          ;var rudderanalytics=window[e];if(Array.isArray(rudderanalytics)){
          if(true===rudderanalytics.snippetExecuted&&window.console&&console.error){
          console.error("RudderStack JavaScript SDK snippet included more than once.")}else{rudderanalytics.snippetExecuted=true,
          window.rudderAnalyticsBuildType="legacy";var sdkBaseUrl="https://cdn.rudderlabs.com";var sdkVersion="v3"
          ;var sdkFileName="rsa.min.js";var scriptLoadingMode="async"
          ;var r=["setDefaultInstanceKey","load","ready","page","track","identify","alias","group","reset","setAnonymousId","startSession","endSession","consent","addCustomIntegration"]
          ;for(var n=0;n<r.length;n++){var t=r[n];rudderanalytics[t]=function(r){return function(){var n
          ;Array.isArray(window[e])?rudderanalytics.push([r].concat(Array.prototype.slice.call(arguments))):null===(n=window[e][r])||void 0===n||n.apply(window[e],arguments)
          }}(t)}try{
          new Function('class Test{field=()=>{};test({prop=[]}={}){return prop?(prop?.property??[...prop]):import("");}}'),
          window.rudderAnalyticsBuildType="modern"}catch(i){}var d=document.head||document.getElementsByTagName("head")[0]
          ;var o=document.body||document.getElementsByTagName("body")[0];window.rudderAnalyticsAddScript=function(e,r,n){
          var t=document.createElement("script");t.src=e,t.setAttribute("data-loader","RS_JS_SDK"),r&&n&&t.setAttribute(r,n),
          "async"===scriptLoadingMode?t.async=true:"defer"===scriptLoadingMode&&(t.defer=true),
          d?d.insertBefore(t,d.firstChild):o.insertBefore(t,o.firstChild)},window.rudderAnalyticsMount=function(){!function(){
          if("undefined"==typeof globalThis){var e;var r=function getGlobal(){
          return"undefined"!=typeof self?self:"undefined"!=typeof window?window:null}();r&&Object.defineProperty(r,"globalThis",{
          value:r,configurable:true})}
          }(),window.rudderAnalyticsAddScript("".concat(sdkBaseUrl,"/").concat(sdkVersion,"/").concat(window.rudderAnalyticsBuildType,"/").concat(sdkFileName),"data-rsa-write-key","${resolved}")
          },
          "undefined"==typeof Promise||"undefined"==typeof globalThis?window.rudderAnalyticsAddScript("https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount"):window.rudderAnalyticsMount()
          ;var loadOptions={};rudderanalytics.load("${resolved}","https://upgradbalcvjs.dataplane.rudderstack.com",loadOptions)}}}();
        `,
      }}
    />
    </>
  );
};

export default RudderStack;
